// The route tree below the root: Desk and Runs (pages without sub-navigation),
// four sections, each a layout route (its sub-navigation + an outlet) with one
// child route per sub-page, plus redirects from the section roots and from
// every retired route.
//
// Lane components are lazy (one chunk each). Route files hold no module-scope
// zod schema except Library's search parsing; zod is pinned to its own leaf
// chunk in vite.config.ts (a chunk cycle through zod blanked the dashboard on
// 2026-09-06).

import {
  createRoute,
  lazyRouteComponent,
  Outlet,
  redirect,
  type AnyRoute,
} from '@tanstack/react-router';
import { z } from 'zod';
import { SectionNav } from '@/components/layout/SectionNav';
import { SECTIONS, type Section } from '@/components/layout/navItems';
import { isRunStatus, type RunStatus } from '@/data/runStatus';
import { Route as RootRoute } from './__root';

function sectionLayout(section: Section) {
  return function SectionLayout(): JSX.Element {
    return (
      <>
        <SectionNav label={section.label} items={section.children} />
        <Outlet />
      </>
    );
  };
}

const section = (to: string): Section => SECTIONS.find((s) => s.to === to)!;

/** /desk?letter=<id>: which strategist letter Desk shows (the latest when absent). */
export function parseDeskSearch(s: Record<string, unknown>): { letter?: number } {
  const n = Number(s['letter']);
  return Number.isSafeInteger(n) && n > 0 ? { letter: n } : {};
}

// Desk is one page: no sub-navigation.
const deskRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: '/desk',
  validateSearch: parseDeskSearch,
  component: lazyRouteComponent(() => import('@/lanes/desk'), 'DeskLane'),
});

/** /runs?status=<status>: the Runs list's filter (every run when absent or unknown). */
export function parseRunsSearch(s: Record<string, unknown>): { status?: RunStatus } {
  const v = s['status'];
  return isRunStatus(v) ? { status: v } : {};
}

// Runs: the list, and one page per run (approve, cancel, steps, outputs, events).
const runsRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: '/runs',
  validateSearch: parseRunsSearch,
  component: lazyRouteComponent(() => import('@/lanes/runs'), 'RunsLane'),
});
const runRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: '/runs/$id',
  component: lazyRouteComponent(() => import('@/lanes/runs'), 'RunLane'),
});

// Literal paths (not SECTIONS[i].to) so the router's path types stay exact.
const videosRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: '/videos',
  component: sectionLayout(section('/videos')),
});
const photosRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: '/photos',
  component: sectionLayout(section('/photos')),
});
const qualityRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: '/quality',
  component: sectionLayout(section('/quality')),
});
const settingsRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: '/settings',
  component: sectionLayout(section('/settings')),
});

function indexRedirect(parent: AnyRoute, to: string) {
  return createRoute({
    getParentRoute: () => parent,
    path: '/',
    beforeLoad: () => {
      throw redirect({ to, replace: true });
    },
  });
}

// ── Videos ───────────────────────────────────────────────────────────────
const LibrarySearch = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  status: z.string().optional(), // comma-joined
  category: z.string().optional(),
  language: z.string().optional(),
  search: z.string().optional(),
});

const videosChildren = [
  indexRedirect(videosRoute, '/videos/library'),
  createRoute({
    getParentRoute: () => videosRoute,
    path: 'library',
    validateSearch: (s) => LibrarySearch.parse(s),
    component: lazyRouteComponent(() => import('@/lanes/videos/library'), 'LibraryLane'),
  }),
  createRoute({
    getParentRoute: () => videosRoute,
    path: 'create',
    component: lazyRouteComponent(() => import('@/lanes/videos/create'), 'CreateLane'),
  }),
  createRoute({
    getParentRoute: () => videosRoute,
    path: 'pipeline',
    component: lazyRouteComponent(() => import('@/lanes/videos/pipeline'), 'PipelineLane'),
  }),
];

// ── Photos ───────────────────────────────────────────────────────────────
const photosChildren = [
  indexRedirect(photosRoute, '/photos/looks'),
  createRoute({
    getParentRoute: () => photosRoute,
    path: 'looks',
    component: lazyRouteComponent(() => import('@/lanes/photos'), 'PhotosLane'),
  }),
  createRoute({
    getParentRoute: () => photosRoute,
    path: 'stickers',
    component: lazyRouteComponent(() => import('@/lanes/photos'), 'StickersLane'),
  }),
  createRoute({
    getParentRoute: () => photosRoute,
    path: 'limits',
    component: lazyRouteComponent(() => import('@/lanes/photos'), 'PhotosLimitsLane'),
  }),
];

// ── Quality ──────────────────────────────────────────────────────────────
const qualityChildren = [
  indexRedirect(qualityRoute, '/quality/label'),
  createRoute({
    getParentRoute: () => qualityRoute,
    path: 'label',
    component: lazyRouteComponent(() => import('@/lanes/quality'), 'LabelLane'),
  }),
  createRoute({
    getParentRoute: () => qualityRoute,
    path: 'overview',
    component: lazyRouteComponent(() => import('@/lanes/quality'), 'OverviewLane'),
  }),
];

// ── Settings ─────────────────────────────────────────────────────────────
const settingsChildren = [
  indexRedirect(settingsRoute, '/settings/budget'),
  createRoute({
    getParentRoute: () => settingsRoute,
    path: 'budget',
    component: lazyRouteComponent(() => import('@/lanes/settings'), 'BudgetLane'),
  }),
  createRoute({
    getParentRoute: () => settingsRoute,
    path: 'app',
    component: lazyRouteComponent(() => import('@/lanes/settings'), 'AppLane'),
  }),
  createRoute({
    getParentRoute: () => settingsRoute,
    path: 'users',
    component: lazyRouteComponent(() => import('@/lanes/settings/users'), 'UserLensLane'),
  }),
];

// ── Redirects: `/` and the retired routes ─────────────────────────────────
export const LEGACY_REDIRECTS: Readonly<Record<string, string>> = {
  '/': '/desk',
  '/videos/review': '/videos/library',
  '/today': '/videos/library',
  '/library': '/videos/library',
  '/lab': '/videos/create',
  '/starbooth': '/photos/looks',
  '/userlens': '/settings/users',
  '/cost': '/settings/budget',
  '/notes': '/videos/library',
  '/health': '/videos/library',
  '/observatory': '/videos/library',
  '/marketing': '/videos/library',
  '/social': '/videos/library',
};

const redirectRoutes = Object.entries(LEGACY_REDIRECTS).map(([path, to]) =>
  createRoute({
    getParentRoute: () => RootRoute,
    path,
    beforeLoad: ({ search }) => {
      // Library filters survive the move (/library?category=… keeps working).
      throw redirect({ to, search: path === '/library' ? search : {}, replace: true });
    },
  }),
);

export const routeTree = RootRoute.addChildren([
  deskRoute,
  runsRoute,
  runRoute,
  videosRoute.addChildren(videosChildren),
  photosRoute.addChildren(photosChildren),
  qualityRoute.addChildren(qualityChildren),
  settingsRoute.addChildren(settingsChildren),
  ...redirectRoutes,
]);
